import React, { useState, useEffect } from "react";
import Layout from "../../../../components/Layouts/Layouts/Layout";
import DashboardHeader from "../../../../components/basic/DashboardHeader";
import { Button, Table, Modal, Spinner } from "react-bootstrap";
import { getURLbyEndPointV2 } from "../../../../store/api";
import axios from "axios";

const RecordingsList = () => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [viewVideo, setViewVideo] = useState(null); // { url, title }
  const [viewSnapshots, setViewSnapshots] = useState(null); // { urls: [], title }
  const [syncing, setSyncing] = useState(false);

  const fetchRecordings = async () => {
    try {
      const res = await axios.get(getURLbyEndPointV2("getAllRecordings"));
      if (res.data.status) {
        setRecordings(res.data.data);
        return res.data.data;
      }
    } catch (error) {
      console.error("Error fetching recordings:", error);
    }
    return [];
  };

  useEffect(() => {
    let interval;
    const init = async () => {
      setLoading(true);
      const recs = await fetchRecordings();
      setLoading(false);

      // Check if any recording needs syncing (STOPPED but no URL) or if there are recordings without snapshots
      const needsSync = recs.some(
        (r) =>
          (r.recordingStatus === "STOPPED" ||
            r.recordingStatus === "UPLOADED") &&
          !r.recordingFileUrl
      );
      if (needsSync) {
        handleSync(recs);
      }
    };
    init();

    // Auto-refresh every 15s to check for updates
    interval = setInterval(async () => {
      await fetchRecordings();
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const handleSync = async (currentRecs) => {
    if (syncing) return;
    setSyncing(true);

    // Filter items that likely need an update
    const toSync = (currentRecs || recordings).filter(
      (r) =>
        (r.recordingStatus === "STOPPED" && !r.recordingFileUrl) ||
        r.recordingStatus === "RECORDING"
    );

    if (toSync.length === 0) {
      setSyncing(false);
      return;
    }

    try {
      // Sync sequentially to avoid rate limits (10/sec)
      for (const rec of toSync) {
        if (rec.meetingRoomId && rec.recordingTaskId) {
          try {
            await axios.post(getURLbyEndPointV2("queryRecordingStatus"), {
              meetingRoomId: rec.meetingRoomId,
            });
          } catch (e) {
            console.error(e);
          }
          // small delay
          await new Promise((r) => setTimeout(r, 200));
        }
      }
      await fetchRecordings();
    } catch (error) {
      console.error("Sync failed", error);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Layout>
      <div className="d-flex flex-column justify-content-center align-items-center px-3">
        <div className="main-width d-flex flex-column">
          <hr />
          <div className="d-flex justify-content-between align-items-center">
            <DashboardHeader
              heading="Arbitration Center / Recordings"
              backUrl="/raise-your-dispute/binding"
            />
            <Button
              variant="primary"
              onClick={() => handleSync(recordings)}
              disabled={syncing || loading}
            >
              {syncing ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Syncing...
                </>
              ) : (
                "Sync Status"
              )}
            </Button>
          </div>
          <hr />

          <Table responsive striped bordered hover>
            <thead>
              <tr className="text-center">
                <th>Date</th>
                <th>Case ID</th>
                <th>Meeting Room ID</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && recordings.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    <Spinner animation="border" />
                  </td>
                </tr>
              ) : recordings.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    No recordings found
                  </td>
                </tr>
              ) : (
                recordings.map((rec, index) => (
                  <tr key={index} className="text-center">
                    <td>{new Date(rec.createdAt).toLocaleString()}</td>
                    <td>{rec.disputeId?.caseId || "N/A"}</td>
                    <td>{rec.meetingRoomId}</td>
                    <td>
                      {rec.recordingStatus === "RECORDING" && (
                        <span className="text-danger fw-bold">● Recording</span>
                      )}
                      {rec.recordingStatus === "PAUSED" && (
                        <span className="text-warning fw-bold">● Paused</span>
                      )}
                      {rec.recordingStatus === "STOPPED" && (
                        <span className="text-info">Processing...</span>
                      )}
                      {rec.recordingStatus === "UPLOADED" && (
                        <span className="text-success">Ready</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex justify-content-center gap-2">
                        {rec.recordingFileUrl ? (
                          <>
                            <Button
                              variant="info"
                              size="sm"
                              onClick={() =>
                                setViewVideo({
                                  url: rec.recordingFileUrl,
                                  title: rec.meetingRoomId,
                                })
                              }
                            >
                              Preview
                            </Button>
                            <a
                              href={rec.recordingFileUrl}
                              target="_blank"
                              rel="noreferrer"
                              download
                            >
                              <Button variant="success" size="sm">
                                Download
                              </Button>
                            </a>
                          </>
                        ) : (
                          <span className="text-muted text-sm align-self-center">
                            {rec.recordingStatus === "RECORDING"
                              ? "In Progress"
                              : "Wait for Sync"}
                          </span>
                        )}

                        {rec.recordingSnapshots &&
                          rec.recordingSnapshots.length > 0 && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                setViewSnapshots({
                                  urls: rec.recordingSnapshots,
                                  title: rec.meetingRoomId,
                                })
                              }
                            >
                              Snapshots ({rec.recordingSnapshots.length})
                            </Button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Video Modal */}
      <Modal
        show={!!viewVideo}
        onHide={() => setViewVideo(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Recording: {viewVideo?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 bg-black d-flex justify-content-center">
          {viewVideo && (
            <video controls style={{ width: "100%", maxHeight: "80vh" }}>
              <source src={viewVideo.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
        </Modal.Body>
      </Modal>

      {/* Snapshots Modal */}
      <Modal
        show={!!viewSnapshots}
        onHide={() => setViewSnapshots(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Snapshots: {viewSnapshots?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-wrap justify-content-center gap-3 p-3">
          {viewSnapshots?.urls?.map((url, i) => (
            <div
              key={i}
              style={{
                maxWidth: "300px",
                border: "1px solid #ddd",
                padding: "5px",
              }}
            >
              <a href={url} target="_blank" rel="noreferrer">
                <img
                  src={url}
                  alt={`Snapshot ${i + 1}`}
                  style={{ width: "100%", height: "auto" }}
                />
              </a>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </Layout>
  );
}; // End Component

export default RecordingsList;
